import { DiffMode, ViewIds } from '@ardoq/api-types';
import {
  DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE,
  DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE,
} from './defaultPersistedSettings';
import {
  DEFAULT_TRAVERSE_SETTINGS,
  DEFAULT_WORKSPACE_MODE_TRAVERSE_SETTINGS,
} from './consts';
import { noop } from 'lodash';
import { CommonDropdownOptions } from '@ardoq/global-consts';
import {
  ViewpointModeViewSettingsShape,
  WorkspaceModeViewSettingsShape,
} from './types';
import { deepFreeze } from '@ardoq/common-helpers';

/**
 * DEFAULT_WORKSPACE_MODE_TRAVERSE_SETTINGS are not needed in viewpoint mode, we therefore
 * don't persist them.
 *
 * However, currently, they are artifacts of the shared view model functions, so we
 * have to maintain them in the app state also in viewpoint mode.
 */
export const DEFAULT_VIEWPOINT_MODE_SETTINGS: ViewpointModeViewSettingsShape =
  deepFreeze({
    [ViewIds.RELATIONSHIPS_3]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.RELATIONSHIPS_3],
      ...DEFAULT_WORKSPACE_MODE_TRAVERSE_SETTINGS,
      highlightDisconnectedComponents: false,
      lastClickedNodeId: null,
      onViewSettingsUpdate: noop,
      isLegendActive: false,
      useNewGrouping: false,
    },
    [ViewIds.MODERNIZED_BLOCK_DIAGRAM]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[
        ViewIds.MODERNIZED_BLOCK_DIAGRAM
      ],
      ...DEFAULT_WORKSPACE_MODE_TRAVERSE_SETTINGS,
      onViewSettingsUpdate: noop,
      isLegendActive: false,
      useNewGrouping: false,
    },
    [ViewIds.BLOCKS]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.BLOCKS],
      ...DEFAULT_WORKSPACE_MODE_TRAVERSE_SETTINGS,
      onViewSettingsUpdate: noop,
      isLegendActive: false,
      activeDiffMode: DiffMode.SCENARIO,
      collapsedGroupIds: [],
      useNewGrouping: false,
      highlightDisconnectedComponents: false,
      lastClickedNodeId: null,
    },
    [ViewIds.TIMELINE]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.TIMELINE],
      ...DEFAULT_TRAVERSE_SETTINGS,
      isLegendActive: false,
      useNewGrouping: false,
    },
    [ViewIds.BUBBLE]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.BUBBLE],
      isLegendActive: false,
    },
    [ViewIds.DEPENDENCY_MAP_2]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.DEPENDENCY_MAP_2],
      ...DEFAULT_TRAVERSE_SETTINGS,
      includeOutgoingReferenceTypeNames: [
        CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD,
      ],
      includeIncomingReferenceTypeNames: [
        CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD,
      ],
      degreesOfChildhood: 0,
      seenTraversalInfo: false,
      useNewGrouping: false,
      onViewSettingsUpdate: noop,
      isLegendActive: false,
    },
    [ViewIds.COMPONENT_MATRIX_2]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[
        ViewIds.COMPONENT_MATRIX_2
      ],
      isLegendActive: false,
    },
    [ViewIds.ASSISTANT_DIAGRAM]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[
        ViewIds.ASSISTANT_DIAGRAM
      ],
      isLegendActive: false,
    },
    [ViewIds.ASSISTANT_BLOCKS]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.ASSISTANT_BLOCKS],
      isLegendActive: false,
    },
    [ViewIds.FLEXIBLE_BLOCK_DIAGRAM]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[
        ViewIds.FLEXIBLE_BLOCK_DIAGRAM
      ],
      isLegendActive: false,
    },
    /* No traverse settings and no `onViewSettingsUpdate`. Both came from `TraversalViewSettings`,
       which these no longer extend: a viewpoint has already decided what is in scope, so the
       settings that describe walking references from a workspace tab were defaulted here only to
       satisfy a type shared with the workspace-mode views. */
    [ViewIds.HIERARCHICAL_TREEMAP]:
      DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[
        ViewIds.HIERARCHICAL_TREEMAP
      ],
    [ViewIds.HIERARCHICAL_PACKING]:
      DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[
        ViewIds.HIERARCHICAL_PACKING
      ],
    [ViewIds.SUNBURST]:
      DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.SUNBURST],
    [ViewIds.SURFACE_3D]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.SURFACE_3D],
      isLegendActive: false,
    },
    [ViewIds.CONTOUR]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.CONTOUR],
      isLegendActive: false,
    },
    [ViewIds.SCATTER_3D]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.SCATTER_3D],
      isLegendActive: false,
    },
    [ViewIds.BUBBLE_3D]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.BUBBLE_3D],
      isLegendActive: false,
    },
    [ViewIds.NETWORK_3D]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.NETWORK_3D],
      /* Off by default, as the other three 3D views are. The legend is Ardoq's own panel and the
         reader turns it on when they want it. */
      isLegendActive: false,
    },
    [ViewIds.SPIDER_2]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.SPIDER_2],
      isLegendActive: false,
    },
    [ViewIds.POLAR]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE[ViewIds.POLAR],
      isLegendActive: false,
    },
  });

export const DEFAULT_WORKSPACE_MODE_SETTINGS: WorkspaceModeViewSettingsShape =
  deepFreeze({
    [ViewIds.PAGESVIEW]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.PAGESVIEW],
    },
    [ViewIds.TAGSCAPE]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.TAGSCAPE],
    },
    [ViewIds.TABLEVIEW]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.TABLEVIEW],
      fullScreen: false,
    },
    [ViewIds.COMPONENTTREE]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.COMPONENTTREE],
    },
    [ViewIds.DEPWHEEL]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.DEPWHEEL],
      isLegendActive: false,
    },
    [ViewIds.TREEMAP]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.TREEMAP],
    },
    [ViewIds.BLOCK_DIAGRAM]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.BLOCK_DIAGRAM],
      onViewSettingsUpdate: noop,
      isLegendActive: false,
    },
    [ViewIds.SWIMLANE]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.SWIMLANE],
      isLegendActive: false,
    },
    [ViewIds.DEPMATRIX]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.DEPMATRIX],
      isExporting: false,
      referenceToWsId: null,
    },
    [ViewIds.ROADMAP]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.ROADMAP],
    },
    [ViewIds.DEPENDENCY_MAP_2]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.DEPENDENCY_MAP_2],
      onViewSettingsUpdate: noop,
    },
    [ViewIds.SPIDER]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.SPIDER],
      // Replace 'sum' with the appropriate ChildAggregationFunction if needed
      aggregateChildValues: 'sum',
      selectedFieldNames: {},
      sideways: false,
      isLegendActive: false,
      includeAllDescendants: false,
    },
    [ViewIds.REFERENCETABLE]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.REFERENCETABLE],
      fullScreen: false,
      isLegendActive: false,
    },
    [ViewIds.CAPABILITY_MAP]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.CAPABILITY_MAP],
      isLegendActive: false,
    },
    [ViewIds.BUBBLE]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.BUBBLE],
    },
    [ViewIds.TIMELINE]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.TIMELINE],
    },
    [ViewIds.COMPONENT_MATRIX]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.COMPONENT_MATRIX],
      isLegendActive: false,
    },
    [ViewIds.NETWORK]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.NETWORK],
      onViewSettingsUpdate: noop,
      isLegendActive: false,
      useNewGrouping: false,
    },
    [ViewIds.ISOMETRIC_BLOCK_DIAGRAM]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[
        ViewIds.ISOMETRIC_BLOCK_DIAGRAM
      ],
      onViewSettingsUpdate: noop,
      isLegendActive: false,
      useNewGrouping: false,
    },
    [ViewIds.RELATIONSHIPS_3]: {
      ...DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE[ViewIds.RELATIONSHIPS_3],
      isLegendActive: false,
      onViewSettingsUpdate: noop,
      useNewGrouping: false,
      lastClickedNodeId: null,
    },
    // #region discontinued views
    [ViewIds.CIRCULAR_RELATIONSHIP_DIAGRAM]: {},
    [ViewIds.SEQUENCE]: {},
    [ViewIds.REFERENCE_AGGREGATION_VIEW]: {},
    [ViewIds.PROCESSFLOW]: {},
    [ViewIds.HEATMAP]: {},
    [ViewIds.API_DOCUMENTATION]: {},
    [ViewIds.RELATIONSHIPS]: {},
    [ViewIds.DEPMAP]: {},
    // #endregion
  });
